import {ReactElement, useEffect, useState} from "react";
import {useProperties} from "../../../contexts/properties/PropertiesContext";
import {Field, Hint, Input, Label, Toggle} from "@zendeskgarden/react-forms";
import PropertyDropdown from "../../bulk-update/components/PropertyDropdown";
import {Button} from "@zendeskgarden/react-buttons";
import {useCreateSendBudgetJobMutation, useGetPropertyBudgetCompletionsLazyQuery} from "../../../__generated__/generated_types";
import * as css from "./styles/SendBudgetForm.module.scss";
import { Add } from "@material-ui/icons";
import {formatterDateISO, formatterTimeHH24MI} from "../../../utils/formatters";

export interface SendBudgetFormProps {
    submitted: () => void;
    budgetYear: number;
    isReforecast?: boolean;
}

export function SendBudgetForm(props: SendBudgetFormProps): ReactElement {
    const isReforecast = props.isReforecast ?? false;
    const defaultTemplate = isReforecast
        ? "{property}_{reforecastYear}_Reforecast"
        : "{property}_{budgetYear}_Budget";
    const templateHint = isReforecast
        ? "Use {property} and {reforecastYear} to auto-fill property and reforecast year."
        : "Use {property} and {budgetYear} to auto-fill property and budget year.";
    const templateLabel = isReforecast ? "Reforecast Name Template" : "Budget Name Template";
    const noteText = isReforecast
        ? "Entrata limits the number of data you can send per minute to 10. Because of this sending reforecasts for many properties at once may take a while."
        : "Entrata limits the number of budgets you can send per minute to 10. Because of this sending budgets for many properties at once may take a while.";

    const [submitJob, {loading}] = useCreateSendBudgetJobMutation({
        fetchPolicy: "no-cache"
    });
    const [fetchBudgetStatus] = useGetPropertyBudgetCompletionsLazyQuery({
        fetchPolicy: "no-cache",
        onCompleted: data => {
            setApprovedPropertyIds(data.getBudgetApprovalStatusForProperties.filter(x => x.approved === true).map(x => x.propertyId));
        }
    });

    const {properties} = useProperties();

    const [propertyIds, setPropertyIds] = useState<string[]>([]);
    const [budgetNameTemplate, setBudgetNameTemplate] = useState<string>(defaultTemplate);
    const [approvedPropertyIds, setApprovedPropertyIds] = useState<string[]>();
    const [onlyForecastedMonths, setOnlyForecastedMonths] = useState<boolean>(false);

    useEffect(() => {
        if (!isReforecast) {
            fetchBudgetStatus({
                variables: {
                    budgetYear: props.budgetYear,
                }
            });
        }
    }, [props.budgetYear, isReforecast]);

    function clear(): void {
        setPropertyIds([]);
        setBudgetNameTemplate(defaultTemplate);
        setOnlyForecastedMonths(false);
    }

    function submit(): void {
        submitJob({
            variables: {
                propertyIds: propertyIds,
                budgetNameTemplate: budgetNameTemplate,
                budgetYear: props.budgetYear,
                isReforecast: isReforecast,
                onlyForecastedMonths: isReforecast ? onlyForecastedMonths : undefined
            }
        }).then(() => {
            clear();
            props.submitted();
        });
    }

    function addAllApprovedProperties() {
        if (!approvedPropertyIds || approvedPropertyIds.length == 0) {
            return;
        }

        setPropertyIds(prevIds => [...prevIds, ...approvedPropertyIds].dedupe());
    }

    return <form className={css.form}>
        <span><b>Note:&nbsp;</b>{noteText}</span>
        <div className={css.field}>
            <div className={css.propertyLabel}>
                <span>Properties</span>
                {!isReforecast &&
                    <Button
                        isBasic
                        size="small"
                        disabled={!approvedPropertyIds || approvedPropertyIds.length == 0}
                        onClick={addAllApprovedProperties}
                    >
                        <Button.StartIcon><Add /></Button.StartIcon>
                        Add Budget Approved Properties
                    </Button>
                }
            </div>
            <PropertyDropdown
                    properties={properties ?? []}
                    onPropertySelect={selected => setPropertyIds(selected)}
                    selectedProperties={propertyIds}
                    lockedProperties={{reforecastLocked: [], budgetLocked: []}}
            />
        </div>
        <div className={css.field}>
        <Field>
            <Label>{templateLabel}</Label>
            <Hint>{templateHint}</Hint>
            <div className={css.buttons}>
                <Button isBasic size="small" onClick={() => setBudgetNameTemplate(prev => prev + "_" + formatterDateISO(new Date()))}>Append current date (YYYYMMDD)</Button>
                <Button isBasic size="small" onClick={() => setBudgetNameTemplate(prev => prev + "_" + formatterTimeHH24MI(new Date()))}>Append current time (HH24MI)</Button>
            </div>
            <Input
                    type={"text"}
                    placeholder={defaultTemplate}
                    value={budgetNameTemplate}
                    onChange={(e) => setBudgetNameTemplate(e.target.value)}
            />
        </Field>
        </div>
        {isReforecast &&
            <div className={css.field}>
                <Field>
                    <Toggle checked={onlyForecastedMonths} onChange={(e) => setOnlyForecastedMonths(e.target.checked)}>
                        <Label>Send only forecasted months</Label>
                        <Hint>When off (default), sends all 12 months of the reforecast year (actuals + forecast).</Hint>
                    </Toggle>
                </Field>
            </div>
        }
        <div className={css.buttons}>
            <Button isBasic onClick={clear} disabled={loading}>Clear</Button>
            <Button isPrimary onClick={submit} disabled={propertyIds.length < 1 || budgetNameTemplate === ""}>Submit</Button>
        </div>
    </form>;
}
